/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  AutocompleteInput,
  DynamicFormInput,
  FormDatePicker,
  FormInput,
  FormSelect,
  FormTextarea,
  FormUpload,
  OpeningHoursEditor,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useEffect, useMemo, useState } from "react";
import { LOG } from "../../utils/logUtil";
import {
  ConceptType,
  HouseholdType,
  IOpeningHoursDay,
  IOpeningHoursException,
  useDropLocations,
} from "../../store/useDropLocations";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { useAuth } from "../../store/useAuth";
import { DropLocationInputItem } from "../../components/form/DynamicFormInput";
import { useMarketPricing } from "../../store/useMarketPricing";
import { useCouriers } from "../../store/useCouriers";
import dayjs from "dayjs";
import { Switch } from "@material-tailwind/react";

interface IAddress {
  address?: string;
  city?: string;
  area?: string;
  type?: string;
  postalCode?: string;
  lat?: string;
  long?: string;
  conceptType?: ConceptType;
  openingDate?: Date;
  highBuildingsRatio?: number;
  inhabitantsPerHousehold?: number;
  averageIncomeInNeighbourhood?: number;
  householdsType?: HouseholdType;
  rentPerYear?: number;
  squareMetersPerStore?: number;
  lockerSpaceUsed?: number;
}

// Google often omits `sublocality` from geocoding results (smaller towns and
// villages). Walk a priority list of address-component types so the Area
// field still resolves to a sensible value instead of coming back empty.
const AREA_COMPONENT_TYPES = [
  "sublocality",
  "neighborhood",
  "postal_town",
  "administrative_area_level_2",
  "locality",
];
const pickArea = (
  components: { types: string[]; long_name: string }[]
): string | undefined => {
  for (const type of AREA_COMPONENT_TYPES) {
    const match = components.find((c) => c.types.includes(type));
    if (match?.long_name) return match.long_name;
  }
  return undefined;
};

let timeout: string | number | NodeJS.Timeout | null | undefined = null;

function EditDropLocationScreen() {
  /** Hooks */
  const params = useParams();
  const navigate = useNavigate();
  const { setAlert } = useAlert();
  const {
    updatedDropLocation,
    clearUpdatedDropLocation,
    updateDropLocation,
    getOneDropLocation,
    oneDropLocation,
    setParcelHandoffEnabled,
    isUpdatingParcelHandoffEnabled,
  } = useDropLocations();
  const currentUser = useAuth((state) => state.user);
  // Only Admin and Super Admin. The backend enforces the same, exactly — a
  // store manager must not be able to open their own store for production
  // intake.
  const canToggleParcelHandoff = [1, 7].includes(
    Number(currentUser?.user_role ?? 0)
  );
  // Changing the location itself (PUT /locations/:id) is Super Admin only.
  // Admins still open this page for the parcel intake switch above.
  const canEditLocation = Number(currentUser?.user_role ?? 0) === 7;
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const { getAllCouriers, allCouriers } = useCouriers();
  /** State */
  const [submitted, setSubmitted] = useState(false);
  const [value, setValue] = useState<{ value: { place_id: string } } | null>(
    null
  );
  const [name, setName] = useState<string | null>(null);
  const [cost_place, setCostPlace] = useState<string | null>(null);
  const [address, setAddress] = useState<IAddress | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [openingHours, setOpeningHours] = useState<IOpeningHoursDay[]>([]);
  const [openingHoursExceptions, setOpeningHoursExceptions] = useState<
    IOpeningHoursException[]
  >([]);
  const [openingHoursNote, setOpeningHoursNote] = useState<{
    en?: string;
    nl?: string;
  }>({});
  const [description, setDescription] = useState<string | null>(null);
  const [acceptedInputs, setAcceptedInputs] = useState<DropLocationInputItem[]>(
    []
  );
  const [notAcceptedInputs, setNotAcceptedInputs] = useState<
    DropLocationInputItem[]
  >([]);
  const [conceptType, setConceptType] = useState<ConceptType | null>(null);
  const [highBuildingsRatio, setHighBuildingsRatio] = useState<string>("");
  const [inhabitantsPerHousehold, setInhabitantsPerNeighbourhood] =
    useState<string>("");
  const [averageIncomeInNeighbourhood, setAverageIncomeInNeighbourhood] =
    useState<string>("");
  const [householdsType, setHouseholdsType] = useState<HouseholdType | null>(
    null
  );
  const [openingDate, setOpeningDate] = useState<Date | null>(null);
  const [rentPerYear, setRentPerYear] = useState<string>("");
  const [squareMetersPerStore, setSquareMetersPerStore] = useState<string>("");
  const [lockerSpaceUsed, setLockerSpaceUsed] = useState<string>("");
  const [isPost, setIsPost] = useState<boolean>(false);
  const [showInApp, setShowInApp] = useState<boolean>(true);
  const [hasBrinksMachines, setHasBrinksMachines] = useState<boolean>(false);
  const [selectedCourierIds, setSelectedCourierIds] = useState<string[]>([]);
  // Starts from the server value and changes locally until Save. Not part of
  // the PUT payload: Save sends it through its own endpoint when it changed.
  const [parcelHandoffEnabled, setParcelHandoffEnabledState] =
    useState<boolean>(false);
  /** Functions */
  /**
   * Saves the parcel switch as part of Save, ahead of the rest of the form.
   *
   * Still its own endpoint: the location PUT has no role gate and ignores the
   * field, so the switch keeps going through the Admin/Super Admin-only PATCH.
   * It runs first so that a refusal stops the Save with the reason on screen,
   * instead of the page closing on a form that only half saved. Disabling asks
   * first - it stops intake at a live store - while enabling does not, because
   * it is the reversible direction.
   *
   * Resolves to false when the Save should stop there.
   */
  const saveParcelHandoffIfChanged = async (): Promise<boolean> => {
    const current = oneDropLocation?.parcelHandoffEnabled ?? false;
    if (
      !canToggleParcelHandoff ||
      !params?.id ||
      parcelHandoffEnabled === current
    ) {
      return true;
    }
    // A second click while the first request is still out: stop quietly
    // rather than report the store's in-flight guard as a failure.
    if (useDropLocations.getState().isUpdatingParcelHandoffEnabled) {
      return false;
    }
    if (
      !parcelHandoffEnabled &&
      // eslint-disable-next-line no-restricted-globals
      !window.confirm(
        "Stop accepting parcel handoffs at this store?\n\nNew registrations will be refused. Existing history, challenge progress and vouchers are kept."
      )
    ) {
      return false;
    }

    const ok = await setParcelHandoffEnabled(
      params.id as string,
      parcelHandoffEnabled
    );
    if (ok) {
      return true;
    }
    // Read the error off the store rather than the destructured value: that
    // one was captured when this handler was created, which is before the
    // request that just failed ever ran, so it is always the previous state -
    // null on the first failure.
    setAlert({
      isOpen: true,
      severity: "error",
      message:
        useDropLocations.getState().parcelHandoffEnabledError ??
        "Could not change parcel intake for this store. Please try again.",
    });
    return false;
  };

  const handleLatitude = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress({ ...address, lat: e.target.value });
  };

  const handleLongitude = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress({ ...address, long: e.target.value });
  };

  useEffect(() => {
    if (!value && address?.lat && address?.long) {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        fetchPlaceByLatLng(Number(address.lat), Number(address.long));
      }, 700);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [address?.lat, address?.long, value]);

  const fetchPlaceByLatLng = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API}`
      );
      const jsonResponse = await response.json();
      if (jsonResponse?.results?.length) {
        const result = jsonResponse.results[0];
        const address = {
          address: result.formatted_address,
          city: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("locality")
          )?.long_name,
          area: pickArea(result.address_components),
          postalCode: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("postal_code")
          )?.long_name,
          lat: result.geometry.location.lat,
          long: result.geometry.location.lng,
        };
        setAddress(address);
      }
    } catch (error) {
      LOG.error("Error on fetching place", { error });
    }
  };

  const fetchPlace = async (placeId: string) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_API}`
      );
      const jsonResponse = await response.json();
      if (jsonResponse?.results?.length) {
        const result = jsonResponse.results[0];
        const address = {
          address: result.formatted_address,
          city: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("locality")
          )?.long_name,
          area: pickArea(result.address_components),
          postalCode: result.address_components.find((c: { types: string[] }) =>
            c.types.includes("postal_code")
          )?.long_name,
          lat: result.geometry.location.lat,
          long: result.geometry.location.lng,
        };
        setAddress(address);
      }
    } catch (error) {
      LOG.error("Error on fetching place", { error });
    }
  };

  /** Effects */
  useEffect(() => {
    if (params?.id) {
      getAllMarketPrices();
      getAllCouriers();
      getOneDropLocation(params.id);
    }
  }, [params?.id]);

  useEffect(() => {
    if (updatedDropLocation) {
      clearUpdatedDropLocation();
      clearUploadedImage();
      navigate(-1);
    }
  }, [updatedDropLocation]);

  useEffect(() => {
    if (value) {
      fetchPlace(value?.value?.place_id);
    }
  }, [value]);

  useEffect(() => {
    if (oneDropLocation) {
      setName(oneDropLocation?.name as string);
      setCostPlace(oneDropLocation?.cost_place as string);
      setAddress(oneDropLocation);
      setUploadedImage(oneDropLocation.image as string);
      setOpeningHours(oneDropLocation?.openingHours ?? []);
      setOpeningHoursExceptions(oneDropLocation?.openingHoursExceptions ?? []);
      setOpeningHoursNote(oneDropLocation?.openingHoursNote ?? {});
      setDescription(oneDropLocation?.description ?? "");
      setAcceptedInputs([
        ...(oneDropLocation.accepting?.map((item, index) => ({
          id: index + 2,
          value: item.name,
        })) ?? []),
      ]);
      setNotAcceptedInputs([
        ...(oneDropLocation.notAccepting?.map((item, index) => ({
          id: index + 2,
          value: item.name,
        })) ?? []),
      ]);
      if (oneDropLocation?.conceptType) {
        setConceptType(oneDropLocation?.conceptType as ConceptType);
      }
      if (oneDropLocation?.highBuildingsRatio)
        setHighBuildingsRatio(String(oneDropLocation?.highBuildingsRatio));
      if (oneDropLocation?.inhabitantsPerHousehold)
        setInhabitantsPerNeighbourhood(
          String(oneDropLocation?.inhabitantsPerHousehold)
        );
      if (oneDropLocation?.averageIncomeInNeighbourhood)
        setAverageIncomeInNeighbourhood(
          String(oneDropLocation?.averageIncomeInNeighbourhood)
        );
      if (oneDropLocation?.householdsType)
        setHouseholdsType(oneDropLocation?.householdsType as HouseholdType);
      if (oneDropLocation?.openingDate)
        setOpeningDate(oneDropLocation?.openingDate as Date);
      if (oneDropLocation?.rentPerYear)
        setRentPerYear(String(oneDropLocation?.rentPerYear));
      if (oneDropLocation?.squareMetersPerStore)
        setSquareMetersPerStore(String(oneDropLocation?.squareMetersPerStore));
      if (oneDropLocation?.lockerSpaceUsed)
        setLockerSpaceUsed(String(oneDropLocation?.lockerSpaceUsed));
      if (oneDropLocation?.isPost) setIsPost(oneDropLocation?.isPost);
      setShowInApp(oneDropLocation?.showInApp ?? false);
      setHasBrinksMachines(oneDropLocation?.hasBrinksMachines ?? false);
      // Absent on a location saved before the field existed, which means off.
      setParcelHandoffEnabledState(
        oneDropLocation?.parcelHandoffEnabled ?? false
      );
      const existingCouriers = (oneDropLocation as any)?.couriers ?? [];
      setSelectedCourierIds(
        existingCouriers.map((c: any) => (typeof c === "string" ? c : c._id))
      );
    }
  }, [oneDropLocation]);
  /** Callbacks */
  const onSubmit = () => {
    if (!canEditLocation) {
      // Only the parcel switch is theirs to save.
      saveParcelHandoffIfChanged().then((proceed) => {
        if (proceed) goBack();
      });
      return;
    }
    if (!name) {
      setAlert({
        isOpen: true,
        message: "Please enter a name.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!cost_place) {
      setAlert({
        isOpen: true,
        message: "Please enter a cost place.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!address) {
      setAlert({
        isOpen: true,
        message: "Please try another address.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body = {
      ...address,
      _id: params?.id,
      name,
      cost_place,
      image: uploadedImage,
      openingHours,
      openingHoursExceptions,
      openingHoursNote,
      description: description ?? "",
      notAccepting:
        notAcceptedInputs?.map((input) => ({ name: input?.value })) ?? [],
      accepting: acceptedInputs?.map((input) => ({ name: input?.value })) ?? [],
      isPost,
      showInApp,
      hasBrinksMachines,
      couriers: selectedCourierIds,
    };
    if (conceptType) body.conceptType = conceptType;
    if (highBuildingsRatio)
      body.highBuildingsRatio = Number(highBuildingsRatio);
    if (inhabitantsPerHousehold)
      body.inhabitantsPerHousehold = Number(inhabitantsPerHousehold);
    if (averageIncomeInNeighbourhood)
      body.averageIncomeInNeighbourhood = Number(averageIncomeInNeighbourhood);
    if (householdsType) body.householdsType = householdsType;
    if (openingDate) body.openingDate = openingDate;
    if (rentPerYear) body.rentPerYear = Number(rentPerYear);
    if (squareMetersPerStore)
      body.squareMetersPerStore = Number(squareMetersPerStore);
    if (lockerSpaceUsed) body.lockerSpaceUsed = Number(lockerSpaceUsed);
    // The parcel switch goes first: if it is refused, nothing else is saved
    // and the page stays open with the reason. Its success refreshes
    // oneDropLocation, which re-fills this form from the server - harmless,
    // because the body above was built before that.
    saveParcelHandoffIfChanged().then((proceed) => {
      if (proceed) updateDropLocation(body);
    });
    LOG.info("Address", { address });
  };
  const goBack = () => navigate(-1);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const marketPricesToSelect = useMemo(() => {
    let marketPricesToSelect = [];
    for (let mp of marketPrices) {
      if (
        !acceptedInputs?.some((input) => input.value === mp.name) &&
        !notAcceptedInputs?.some((input) => input.value === mp.name)
      ) {
        marketPricesToSelect.push({
          value: mp._id ?? "",
          label: mp.name,
        });
      }
    }
    return marketPricesToSelect;
  }, [acceptedInputs, notAcceptedInputs, marketPrices]);

  const selectConceptType = (e: any) => setConceptType(e?.target?.value);
  const selectHouseholdType = (e: any) => setHouseholdsType(e?.target?.value);

  const imageError = submitted && !image;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Edit drop location
        </h1>
      </div>
      {!canEditLocation ? (
        <p className="font-satoshi text-sm text-gray-text mb-6">
          Only super admins can change location details, so they are read-only
          here. Saving here only saves the parcel handoff setting.
        </p>
      ) : null}
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Drop location
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <div className="text-base text-gray-700 font-satoshi mb-6 flex flex-row items-center">
              Weeks since opening:{" "}
              <div className="font-bold mx-1">
                {" "}
                {dayjs().diff(
                  oneDropLocation?.openingDate ?? new Date(),
                  "week"
                )}{" "}
              </div>
              weeks
            </div>
            <ReadOnlyFields readOnly={!canEditLocation}>
              <FormInput
                placeholder="Type name here..."
                label="Name"
                error={submitted && !name}
                value={name ?? ""}
                onChange={(e) => setName(e.target.value)}
              />
              <FormInput
                placeholder="Type cost place here..."
                label="Cost Place"
                error={submitted && !cost_place}
                value={cost_place ?? ""}
                onChange={(e) => setCostPlace(e.target.value)}
              />
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="PostNL Service"
                checked={isPost}
                onChange={(e) => setIsPost(e?.target.checked)}
              />
              <AutocompleteInput
                value={value}
                onChange={setValue}
                label="Select drop place"
              />
              <FormInput
                placeholder="Address will be filled here..."
                label="Address"
                disabled
                error={submitted && !address?.address}
                value={address?.address ?? ""}
              />
              <FormInput
                placeholder="City will be filled here..."
                label="City"
                disabled
                error={submitted && !address?.city}
                value={address?.city ?? ""}
              />
              <FormSelect
                label="Select Location concept type"
                placeholder="Select concept type"
                value={conceptType ?? ""}
                // @ts-ignore
                options={
                  Object.entries(ConceptType).map(([key, value]) => ({
                    value: value,
                    label: key,
                  })) ?? []
                }
                // error={submitted && !conceptType}
                onChange={selectConceptType}
              />
              <FormDatePicker
                label="Store Opening date"
                placeholder="Select date"
                value={openingDate ?? new Date()}
                onChange={setOpeningDate}
                labelClassName="mt-4"
              />
              <FormInput
                placeholder="High buildings ratio (Hoogbouw ratio / stedelijkheidsklasse)"
                label="High buildings ratio"
                type="number"
                onChange={(e) => setHighBuildingsRatio(e.target.value)}
                // error={!!highBuildingsRatio && isNaN(highBuildingsRatio)}
                value={highBuildingsRatio ?? "}"}
              />
              <FormInput
                placeholder="Inhabitants per household"
                label="Inhabitants per household"
                type="number"
                onChange={(e) => setInhabitantsPerNeighbourhood(e.target.value)}
                // error={submitted && !inhabitantsPerHousehold}
                value={inhabitantsPerHousehold ?? ""}
              />
              <FormInput
                placeholder="Average income in neighbourhood"
                label="Average income in neighbourhood"
                type="number"
                onChange={(e) =>
                  setAverageIncomeInNeighbourhood(e.target.value)
                }
                // error={submitted && !averageIncomeInNeighbourhood}
                value={averageIncomeInNeighbourhood ?? ""}
              />
              <FormSelect
                label="Select household type"
                placeholder="Select household type"
                value={householdsType ?? ""}
                // @ts-ignore
                options={
                  Object.entries(HouseholdType).map(([key, value]) => ({
                    value: value,
                    label: key,
                  })) ?? []
                }
                // error={submitted && !householdsType}
                onChange={selectHouseholdType}
              />
              <FormInput
                placeholder="Area will be filled here..."
                label="Area"
                disabled
                error={submitted && !address?.area}
                value={address?.area ?? ""}
              />
              <FormInput
                placeholder="Postal code will be filled here..."
                label="Postal code"
                disabled
                error={submitted && !address?.postalCode}
                value={address?.postalCode ?? ""}
              />
              <FormInput
                placeholder="Latitude will be filled here..."
                label="Latitude"
                // disabled
                error={submitted && !address?.lat}
                value={address?.lat ?? ""}
                onChange={handleLatitude}
              />
              <FormInput
                placeholder="Longitude will be filled here..."
                label="Longitude"
                // disabled
                error={submitted && !address?.long}
                value={address?.long ?? ""}
                onChange={handleLongitude}
              />
              <OpeningHoursEditor
                label="Opening hours"
                value={openingHours}
                onChange={setOpeningHours}
                exceptions={openingHoursExceptions}
                onExceptionsChange={setOpeningHoursExceptions}
                note={openingHoursNote}
                onNoteChange={setOpeningHoursNote}
              />
              <FormTextarea
                placeholder="Type a short description shown in the mobile app..."
                label="Location description"
                value={description ?? ""}
                onChange={(e) => setDescription(e.target.value)}
              />
              <FormInput
                placeholder="Rent per year"
                label="Rent per year"
                type="number"
                onChange={(e) => setRentPerYear(e.target.value)}
                value={rentPerYear ?? ""}
              />
              <FormInput
                placeholder="Square meters per store"
                label="Square meters per store"
                type="number"
                onChange={(e) => setSquareMetersPerStore(e.target.value)}
                value={squareMetersPerStore ?? ""}
              />
              <FormInput
                placeholder="Locker space used"
                label="Locker space used"
                type="number"
                onChange={(e) => setLockerSpaceUsed(e.target.value)}
                value={lockerSpaceUsed ?? ""}
              />
              <DynamicFormInput
                label="Accepted items"
                inputs={acceptedInputs}
                setInputs={setAcceptedInputs}
                options={marketPricesToSelect ?? []}
              />
              <DynamicFormInput
                label="Not accepted items"
                inputs={notAcceptedInputs}
                setInputs={setNotAcceptedInputs}
                options={marketPricesToSelect ?? []}
              />
              <Switch
                crossOrigin={"anonymous"}
                color="teal"
                ripple
                label="Show this location in app"
                checked={showInApp}
                onChange={(e) => setShowInApp(e?.target.checked)}
              />
              <div className="mt-4">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  label="Has Brinks Machines"
                  checked={hasBrinksMachines}
                  onChange={(e) => setHasBrinksMachines(e?.target.checked)}
                />
              </div>
            </ReadOnlyFields>
            {/* Its own block, though saved with the form's Save. Unrelated to
                "Show this location in app": a store hidden from the customer
                map can still be a parcel collection point. */}
            {canToggleParcelHandoff ? (
              <div className="mt-6 border-t border-gray-border pt-6">
                <p className="font-satoshi font-medium text-base text-dark mb-3">
                  Parcel handoffs
                </p>
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  label="Enable parcel handoffs at this store"
                  checked={parcelHandoffEnabled}
                  disabled={isUpdatingParcelHandoffEnabled}
                  onChange={(e) =>
                    setParcelHandoffEnabledState(e?.target.checked)
                  }
                />
                <p className="mt-2 ml-14 text-xs text-gray-600">
                  When enabled, this store can be selected during parcel
                  pickup/drop-off registration. Disabling it stops new
                  registrations but keeps existing history. Saved with the Save
                  button below.
                </p>
              </div>
            ) : null}
            <ReadOnlyFields readOnly={!canEditLocation}>
              {allCouriers.length > 0 && (
                <div className="mt-6">
                  <p className="font-satoshi font-medium text-sm text-dark mb-2">
                    Legacy bulk parcel couriers
                  </p>
                  <p className="text-xs text-gray-600 mb-2">
                    Part of the older aggregate parcel statistics. Unrelated to
                    the parcel handoff switch above — carriers for that are
                    configured on the Partner, not here.
                  </p>
                  <div className="flex flex-col gap-2">
                    {allCouriers.map((courier: any) => (
                      <label
                        key={courier._id}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-teal-500"
                          checked={selectedCourierIds.includes(courier._id)}
                          onChange={(e) => {
                            setSelectedCourierIds((prev) =>
                              e.target.checked
                                ? [...prev, courier._id]
                                : prev.filter((id) => id !== courier._id)
                            );
                          }}
                        />
                        <span className="font-satoshi text-sm text-gray-700">
                          {courier.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </ReadOnlyFields>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton onClick={onSubmit} className="ml-4" label="Save" />
            </div>
          </div>
        </div>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white rounded-xl border-gray-border drop-shadow-none h-[360px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Drop Location photo
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ReadOnlyFields readOnly={!canEditLocation}>
              <FormUpload
                onChange={onImageChange}
                value={image}
                error={imageError}
                imageUrl={uploadedImage as string}
                accept=".png, .jpg, .jpeg, .webp"
                formatsHint="PNG, JPG or WebP"
              />
            </ReadOnlyFields>
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              {canEditLocation ? (
                <PrimaryButton
                  onClick={onUploadImage}
                  className="ml-4"
                  label="Save"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Makes the location fields read-only for roles that may open this page but
 * not change the location (Admins, who only manage parcel intake here). A
 * disabled fieldset disables every native input inside it; pointer-events
 * covers third-party widgets (address autocomplete, switches, date picker)
 * that do not follow it.
 */
const ReadOnlyFields = ({
  readOnly,
  children,
}: {
  readOnly: boolean;
  children: React.ReactNode;
}) => (
  <fieldset
    disabled={readOnly}
    aria-readonly={readOnly}
    className={`min-w-0 ${readOnly ? "pointer-events-none opacity-70" : ""}`}
  >
    {children}
  </fieldset>
);

export default withProtectedRoute(EditDropLocationScreen);
